import React, { useState } from "react";
import { Check, ChevronDown, Info, MessageCircle, Mic, Paperclip, X } from "lucide-react";

export const primaryBtn =
  "px-4 py-2.5 text-base whitespace-nowrap text-white bg-indigo-700 hover:bg-indigo-800 rounded-lg shadow-sm transition-colors";
export const ghostBtn =
  "px-4 py-2.5 text-base whitespace-nowrap text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors";
export const inputCls =
  "w-full px-4 py-3 text-base rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/30";

const STEP_LABELS = ["Choose Type", "Configure", "Preview", "Save and Use"];

export const Stepper: React.FC<{ current: number }> = ({ current }) => (
  <div className="flex items-center gap-4 px-6 pb-5 overflow-x-auto">
    {STEP_LABELS.map((label, i) => {
      const n = i + 1;
      const done = n < current;
      const active = n === current;
      return (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2.5 shrink-0">
            {done ? (
              <span className="w-8 h-8 rounded-full bg-emerald-100 border border-emerald-500 flex items-center justify-center">
                <Check size={16} className="text-emerald-600" />
              </span>
            ) : active ? (
              <span className="w-7 h-7 rounded-md bg-emerald-200 border border-emerald-500 text-sm font-medium text-gray-800 flex items-center justify-center">{n}</span>
            ) : (
              <span className="w-7 h-7 rounded-md bg-indigo-50 dark:bg-indigo-500/15 text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center">{n}</span>
            )}
            <span className={`text-base ${done || active ? "text-gray-900 dark:text-gray-100" : "text-gray-400 dark:text-gray-500"}`}>{label}</span>
          </div>
          {i < STEP_LABELS.length - 1 && (
            <span className={`h-px flex-1 min-w-[24px] ${n <= current ? "bg-gray-800 dark:bg-gray-300" : "bg-indigo-100 dark:bg-indigo-500/20"}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

export const SectionHeader: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <div className="mb-6">
    <h2 className="text-2xl text-gray-900 dark:text-gray-100">{title}</h2>
    <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
  </div>
);

export const Field: React.FC<{ label: string; info?: string; hint?: string; className?: string; children: React.ReactNode }> = ({
  label,
  info,
  hint,
  className,
  children,
}) => (
  <div className={className}>
    <label className="flex items-center gap-1.5 text-base text-gray-800 dark:text-gray-200">
      {label}
      {info && (
        <span title={info}>
          <Info size={15} className="text-indigo-700" />
        </span>
      )}
    </label>
    {hint && <p className="text-sm text-gray-600 dark:text-gray-400">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

export const SelectBox: React.FC<{ value: string; onChange: (v: string) => void; options: string[] }> = ({ value, onChange, options }) => (
  <div className="relative">
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} appearance-none pr-10`}>
      {!options.includes(value) && value && <option value={value}>{value}</option>}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-800 dark:text-gray-200 pointer-events-none" />
  </div>
);

export const Card: React.FC<{ title?: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="rounded-xl bg-white dark:bg-gray-900 px-4 py-4">
    {title && <p className="text-base text-gray-900 dark:text-gray-100">{title}</p>}
    {subtitle && <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>}
    <div className={title ? "mt-3" : ""}>{children}</div>
  </div>
);

export const InfoChip: React.FC<{ value: string; tone?: "indigo" | "green" | "gray" }> = ({ value, tone = "indigo" }) => (
  <span
    className={`inline-block text-sm rounded px-2 py-1 text-gray-800 dark:text-gray-100 ${
      tone === "green" ? "bg-green-100 dark:bg-green-500/15" : tone === "gray" ? "bg-gray-100 dark:bg-gray-800" : "bg-indigo-50 dark:bg-indigo-500/15"
    }`}
  >
    {value}
  </span>
);

interface ChatMessage {
  from: "ai" | "user";
  text: string;
}

// Static side panel — ConvoAI isn't connected to the trait builder yet.
export const AiPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { from: "ai", text: "I've drafted this trait from your selection. Tell me if you'd like a different source, logic or name." },
  ]);
  const [draft, setDraft] = useState("");

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    setMessages((m) => [...m, { from: "user", text }, { from: "ai", text: "I can't change the trait from here yet — ConvoAI isn't connected to this flow." }]);
    setDraft("");
  };

  return (
    <aside className="bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800 flex flex-col min-h-[420px] h-full">
      <div className="bg-lime-50 dark:bg-gray-800 px-5 py-4 flex items-center justify-between">
        <h3 className="text-xl text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
          <MessageCircle size={22} /> ConvoAI
        </h3>
        <button type="button" onClick={onClose} title="Close" className="text-gray-800 dark:text-gray-200">
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`text-sm rounded-xl px-4 py-3 border ${
              m.from === "ai"
                ? "border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                : "border-indigo-100 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10 text-gray-800 dark:text-gray-100 ml-8"
            }`}
          >
            {m.text}
          </div>
        ))}
      </div>
      <div className="p-4">
        <div className="border border-gray-300 dark:border-gray-700 rounded-lg p-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Ask anything. . ."
            className="w-full resize-none text-sm bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500"
          />
          <div className="flex items-center justify-end gap-3 mt-1">
            <Mic size={18} className="text-gray-700 dark:text-gray-300" />
            <Paperclip size={18} className="text-gray-700 dark:text-gray-300" />
            <button type="button" onClick={send} className="px-3 py-1.5 text-sm text-white bg-indigo-700 hover:bg-indigo-800 rounded-md">
              Send
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};
